/* Admin console UI kit. Every section is built from these pieces so spacing,
   type sizes, colours and states stay the same everywhere. Colour is reserved
   for meaning (success, warning, danger, info); everything else is neutral. */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronLeft, ChevronRight,
  Copy, Inbox, RefreshCw, Search, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { errorMessage, initials, isoDay, parseDate, timeAgo } from './lib';

/* ── Controls ────────────────────────────────────────────────────────────── */

export const Btn = React.forwardRef(({ className, ...props }, ref) => (
  <Button ref={ref} className={cn('rounded-md shadow-none', className)} {...props} />
));
Btn.displayName = 'Btn';

export const TextInput = React.forwardRef(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'h-9 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground',
      'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/15',
      'focus-visible:border-foreground/30 disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
TextInput.displayName = 'TextInput';

export function SearchInput({ value, onChange, placeholder = 'Search', className, autoFocus }) {
  return (
    <div className={cn('relative w-full sm:w-72', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <TextInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-8"
        autoFocus={autoFocus}
        type="search"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/* A row of mutually exclusive options; each option may carry a count. */
export function Segmented({ value, onChange, options, className, size = 'sm' }) {
  return (
    <div className={cn('inline-flex max-w-full items-center overflow-x-auto rounded-lg border border-border bg-card p-0.5', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-md font-medium transition-colors',
              size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-[13px]',
              active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={active}
          >
            {o.label}
            {o.count !== undefined && (
              <span className={cn('tabular text-[11px]', active ? 'text-muted-foreground' : 'text-muted-foreground/70')}>
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function RefreshButton({ onClick, loading, updatedAt, label = 'Refresh' }) {
  const button = (
    <Btn variant="outline" size="sm" onClick={onClick} disabled={loading}>
      <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
      <span className="hidden sm:inline">{label}</span>
    </Btn>
  );
  if (!updatedAt) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild><span className="inline-flex">{button}</span></TooltipTrigger>
      <TooltipContent>Updated {timeAgo(updatedAt)}</TooltipContent>
    </Tooltip>
  );
}

export function CopyButton({ text, label, className }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(text || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Could not copy to the clipboard');
    }
  };
  return (
    <Btn variant="ghost" size="sm" onClick={copy} className={cn('h-7 px-2 text-xs', className)} disabled={!text}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label && <span>{copied ? 'Copied' : label}</span>}
    </Btn>
  );
}

/* ── Layout ──────────────────────────────────────────────────────────────── */

export function Page({ title, description, actions, children }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Panel({ title, description, actions, children, className, bodyClassName, flush = false }) {
  return (
    <section className={cn('min-w-0 rounded-xl border border-border bg-card', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-foreground">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn(flush ? '' : 'p-4', bodyClassName)}>{children}</div>
    </section>
  );
}

export function StatGrid({ children, className }) {
  return <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-4', className)}>{children}</div>;
}

const VALUE_TONE = {
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  danger: 'text-red-600',
  info: 'text-sky-600',
};

export function StatCard({ label, value, hint, tone, onClick, loading, className }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'min-w-0 rounded-xl border border-border bg-card p-4 text-left',
        onClick && 'transition-colors hover:border-foreground/20 hover:bg-secondary/40',
        className,
      )}
    >
      <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
      {loading ? (
        <Skeleton className="mt-2.5 h-7 w-24" />
      ) : (
        <p className={cn('mt-2 truncate text-2xl font-semibold leading-tight tracking-tight tabular', VALUE_TONE[tone])}>
          {value}
        </p>
      )}
      {hint && <p className="mt-1 truncate text-xs text-muted-foreground">{hint}</p>}
    </Comp>
  );
}

const PILL_TONE = {
  neutral: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
  danger: 'border-red-200 bg-red-50 text-red-700',
  info: 'border-sky-200 bg-sky-50 text-sky-700',
  accent: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  solid: 'border-zinc-900 bg-zinc-900 text-white',
};

export function Pill({ tone = 'neutral', children, className, dot }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4',
        PILL_TONE[tone] || PILL_TONE.neutral,
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
}

export function Avatar({ user, size = 32 }) {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size };
  if (user?.picture && !failed) {
    return (
      <img
        src={user.picture}
        alt=""
        style={style}
        className="shrink-0 rounded-full border border-border object-cover"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      style={{ ...style, fontSize: Math.round(size * 0.4) }}
      className="flex shrink-0 items-center justify-center rounded-full bg-zinc-200 font-semibold text-zinc-700"
    >
      {initials(user)}
    </span>
  );
}

/* Label/value pairs, two columns on wide screens. */
export function KeyValue({ items, columns = 2 }) {
  const shown = items.filter(Boolean);
  return (
    <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-3', columns === 2 && 'sm:grid-cols-2', columns === 3 && 'sm:grid-cols-3')}>
      {shown.map(({ label, value, mono }) => (
        <div key={label} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className={cn('mt-0.5 break-words text-sm text-foreground', mono && 'font-mono text-xs')}>
            {value === null || value === undefined || value === '' ? <span className="text-muted-foreground/60">—</span> : value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ── States ──────────────────────────────────────────────────────────────── */

export function EmptyState({ icon: Icon = Inbox, title, description, action, className }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </span>
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      {description && <p className="mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = 'Could not load this data', className }) {
  return (
    <div className={cn('flex items-start gap-3 rounded-xl border border-red-200 bg-red-50/60 p-4', className)}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-red-900">{title}</p>
        <p className="mt-0.5 break-words text-xs text-red-800/80">{errorMessage(error)}</p>
      </div>
      {onRetry && (
        <Btn variant="outline" size="sm" onClick={onRetry} className="shrink-0 border-red-200 bg-white">
          Retry
        </Btn>
      )}
    </div>
  );
}

export function Notice({ tone = 'warning', icon: Icon = AlertTriangle, title, children, action }) {
  const tones = {
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
    info: 'border-sky-200 bg-sky-50 text-sky-900',
    danger: 'border-red-200 bg-red-50 text-red-900',
    neutral: 'border-border bg-secondary/60 text-foreground',
  };
  return (
    <div className={cn('flex flex-col gap-3 rounded-xl border p-3.5 sm:flex-row sm:items-center', tones[tone])}>
      <div className="flex min-w-0 flex-1 items-start gap-2.5">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
        <div className="min-w-0 text-sm">
          {title && <p className="font-medium">{title}</p>}
          {children && <div className="text-xs leading-relaxed opacity-80">{children}</div>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function StatSkeletons({ count = 4, className }) {
  return (
    <StatGrid className={className}>
      {Array.from({ length: count }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-xl" />)}
    </StatGrid>
  );
}

export function RowsSkeleton({ rows = 6, className }) {
  return (
    <div className={cn('space-y-2 p-4', className)}>
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9 w-full rounded-md" />)}
    </div>
  );
}

/* ── Tables ──────────────────────────────────────────────────────────────── */

export function Table({ children, className }) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)}>{children}</table>
    </div>
  );
}

export function TH({ children, className, align = 'left' }) {
  return (
    <th
      className={cn(
        'h-9 whitespace-nowrap border-b border-border px-4 text-xs font-medium text-muted-foreground',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function SortTH({ label, field, sort, onSort, align = 'left', className }) {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TH align={align} className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          active && 'text-foreground',
          align === 'right' && 'flex-row-reverse',
        )}
      >
        {label}
        <Icon className={cn('h-3 w-3', !active && 'opacity-40')} />
      </button>
    </TH>
  );
}

export function TR({ children, onClick, className }) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        'border-b border-border last:border-0',
        onClick && 'cursor-pointer transition-colors hover:bg-secondary/50',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function TD({ children, className, align = 'left' }) {
  return (
    <td className={cn('px-4 py-2.5 align-middle', align === 'right' && 'text-right tabular', className)}>
      {children}
    </td>
  );
}

export function useSort(initialField, initialDir = 'desc') {
  const [sort, setSort] = useState({ field: initialField, dir: initialDir });
  const onSort = useCallback((field) => {
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }));
  }, []);
  return [sort, onSort];
}

export function sortRows(rows, { field, dir }, getters = {}) {
  const get = getters[field] || ((r) => r[field]);
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let va = get(a);
    let vb = get(b);
    if (va === null || va === undefined || va === '') return 1;
    if (vb === null || vb === undefined || vb === '') return -1;
    if (typeof va === 'string') va = va.toLowerCase();
    if (typeof vb === 'string') vb = vb.toLowerCase();
    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });
}

export function usePagination(items, pageSize = 50, resetKey) {
  const [page, setPage] = useState(0);
  const lastKey = useRef(resetKey);
  if (lastKey.current !== resetKey) {
    lastKey.current = resetKey;
    if (page !== 0) setPage(0);
  }
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pageCount - 1);
  const slice = useMemo(
    () => items.slice(current * pageSize, current * pageSize + pageSize),
    [items, current, pageSize],
  );
  return { page: current, pageCount, setPage, slice, total: items.length, pageSize };
}

export function Pagination({ page, pageCount, setPage, total, pageSize, noun = 'rows' }) {
  if (total === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, from + pageSize - 1);
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
      <span className="tabular">{from}–{to} of {total.toLocaleString('en-US')} {noun}</span>
      {pageCount > 1 && (
        <div className="flex items-center gap-1">
          <Btn variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page">
            <ChevronLeft className="h-4 w-4" />
          </Btn>
          <span className="tabular px-1">{page + 1} / {pageCount}</span>
          <Btn variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} aria-label="Next page">
            <ChevronRight className="h-4 w-4" />
          </Btn>
        </div>
      )}
    </div>
  );
}

/* ── Date range ──────────────────────────────────────────────────────────── */

export const RANGE_PRESETS = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '30d', label: '30 days', days: 30 },
  { value: '90d', label: '90 days', days: 90 },
];

const daysBetween = (a, b) => Math.round((parseDate(b) - parseDate(a)) / 86400000) + 1;

/* A date window: a preset (last N days, today included) or a custom range. */
export function useDateRange(initial = '30d', presets = RANGE_PRESETS) {
  const presetRange = useCallback((value) => {
    const p = presets.find((x) => x.value === value);
    const end = new Date();
    const start = new Date();
    if (p?.days) start.setDate(start.getDate() - (p.days - 1));
    else start.setFullYear(2020, 0, 1); // "All time"
    return { start: isoDay(start), end: isoDay(end) };
  }, [presets]);

  const [preset, setPresetState] = useState(initial);
  const [range, setRange] = useState(() => presetRange(initial));

  const setPreset = (value) => {
    setPresetState(value);
    if (value !== 'custom') setRange(presetRange(value));
  };
  const setDates = (next) => {
    setPresetState('custom');
    setRange((r) => ({ ...r, ...next }));
  };

  let { start, end } = range;
  if (start && end && start > end) [start, end] = [end, start];
  const days = start && end ? Math.max(1, daysBetween(start, end)) : 30;
  return { preset, setPreset, start, end, days, setDates, presets };
}

export function DateRangeControl({ range }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented
        value={range.preset}
        onChange={range.setPreset}
        options={[...range.presets, { value: 'custom', label: 'Custom' }]}
      />
      <div className="flex items-center gap-1.5">
        <TextInput
          type="date"
          value={range.start}
          max={range.end}
          onChange={(e) => e.target.value && range.setDates({ start: e.target.value })}
          className="h-8 w-[138px] px-2 text-xs"
          aria-label="Start date"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <TextInput
          type="date"
          value={range.end}
          min={range.start}
          onChange={(e) => e.target.value && range.setDates({ end: e.target.value })}
          className="h-8 w-[138px] px-2 text-xs"
          aria-label="End date"
        />
      </div>
    </div>
  );
}

/* ── Bars ────────────────────────────────────────────────────────────────── */

/* Horizontal bar list: label, bar, count and share. */
export function BarList({ items, empty = 'No data yet', valueFormatter, onItemClick }) {
  if (!items.length) return <p className="py-6 text-center text-xs text-muted-foreground">{empty}</p>;
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="space-y-2.5">
      {items.map((item) => (
        <div
          key={item.label}
          className={cn('min-w-0', onItemClick && 'cursor-pointer')}
          onClick={onItemClick ? () => onItemClick(item) : undefined}
        >
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[13px]">
            <span className="truncate text-foreground">{item.label}</span>
            <span className="shrink-0 tabular text-xs text-muted-foreground">
              {valueFormatter ? valueFormatter(item.value) : item.value.toLocaleString('en-US')}
              {item.share !== undefined && <span className="ml-1.5 text-muted-foreground/70">{Math.round(item.share)}%</span>}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full"
              style={{ width: `${(item.value / max) * 100}%`, background: item.color || '#27272a' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Confirmation ────────────────────────────────────────────────────────── */

/* const [confirm, confirmDialog] = useConfirm();
   if (await confirm({ title, description, confirmLabel, destructive })) … */
export function useConfirm() {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    resolver.current = resolve;
    setRequest(options);
  }), []);

  const settle = (result) => {
    resolver.current?.(result);
    resolver.current = null;
    setRequest(null);
  };

  const dialog = (
    <AlertDialog open={!!request} onOpenChange={(open) => { if (!open) settle(false); }}>
      <AlertDialogContent className="max-w-md rounded-xl sm:rounded-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">{request?.title}</AlertDialogTitle>
          {request?.description && (
            <AlertDialogDescription className="text-sm leading-relaxed">{request.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-md shadow-none" onClick={() => settle(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              'rounded-md shadow-none',
              request?.destructive && buttonVariants({ variant: 'destructive' }),
              request?.destructive && 'rounded-md shadow-none',
            )}
            onClick={() => settle(true)}
          >
            {request?.confirmLabel || 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [confirm, dialog];
}
